import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Wrench, Zap, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { MaskedLines, Reveal, ImageReveal } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import EditorialMarquee from "@/components/EditorialMarquee";
import { useSite, useSiteLoaded } from "@/context/SiteContext";
import SmartLink from "@/components/SmartLink";

const ARTIST_IMG =
  "https://images.unsplash.com/photo-1524230659092-07f99a75c013?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const site = useSite();
  const h = site.home || {};
  const contentLoaded = useSiteLoaded();
  const [featured, setFeatured] = useState([]);
  const [artists, setArtists] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [0, 12]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 4 } }).then((r) => setFeatured(r.data)).catch(() => {});
    api.get("/artists", { params: { featured: true } }).then((r) => setArtists(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen overflow-hidden bg-white pt-20 grain">
        <div className="mx-auto grid min-h-[calc(100vh-5rem)] max-w-[1500px] grid-cols-1 items-center gap-8 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-7 z-10">
            {contentLoaded && (
            <>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="mb-6 flex items-center gap-3 font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra"
            >
              <span className="h-px w-10 bg-gavra" /> {h.hero_eyebrow}
            </motion.p>

            <h1 className="font-heading text-6xl font-black uppercase leading-[0.85] tracking-tighter text-black md:text-8xl lg:text-[9vw]">
              <MaskedLines lines={[h.hero_line1, h.hero_line2, <span key="r" className="text-gavra">{h.hero_line3_accent}</span>]} />
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-10 max-w-lg"
            >
              <p className="text-base font-medium leading-relaxed text-black/60 md:text-lg">
                {h.hero_description}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <SmartLink
                  to={h.cta1_url}
                  testid="hero-shop-btn"
                  className="group inline-flex items-center gap-3 bg-black px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra"
                >
                  {h.cta1_label}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </SmartLink>
                <SmartLink
                  to={h.cta2_url}
                  testid="hero-cta-2"
                  className="inline-flex items-center gap-2 border border-black px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-black transition-colors hover:bg-black hover:text-white"
                >
                  {h.cta2_label}
                </SmartLink>
              </div>
            </motion.div>
            </>
            )}
          </div>

          <div className="relative lg:col-span-5 h-[50vh] lg:h-[80vh]">
            <motion.div
              style={{ y: imgY, rotate: imgRotate, scale: imgScale }}
              className="absolute inset-0 border border-black/10 overflow-hidden"
            >
              <img src={h.hero_image} alt="Drum hardware" className="h-full w-full object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="absolute -bottom-4 -left-4 z-10 bg-gavra px-5 py-3 font-mono-hw text-[11px] font-bold uppercase tracking-widest text-white"
            >
              Built for stage
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
        >
          <span className="font-mono-hw text-[10px] uppercase tracking-[0.3em] text-black/40">Scroll</span>
          <span className="h-10 w-px bg-black/30" />
        </motion.div>
      </section>

      {/* MARQUEE */}
      <EditorialMarquee text={h.marquee || []} speed={40} />

      {/* FEATURE STRIP */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
          {[
            { icon: Wrench, ...(h.features?.[0] || {}) },
            { icon: Zap, ...(h.features?.[1] || {}) },
            { icon: ShieldCheck, ...(h.features?.[2] || {}) },
          ].map((f, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border-t-2 border-black pt-6">
                <f.icon className="h-8 w-8 text-gavra" strokeWidth={2} />
                <h3 className="mt-5 font-heading text-2xl font-bold uppercase tracking-tight">{f.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-black/60">{f.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="mx-auto max-w-[1500px] px-5 md:px-10 pb-24 md:pb-32">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 border-b border-black/10 pb-8 md:flex-row md:items-end">
            <div>
              <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">{h.featured_eyebrow}</p>
              <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase leading-none tracking-tight md:text-6xl">
                {h.featured_title}
              </h2>
            </div>
            <Link to="/products" className="link-underline flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-black">
              View all products <ArrowUpRight className="h-4 w-4 text-gavra" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((p, i) => (
            <ImageReveal key={p.id}>
              <ProductCard product={p} />
            </ImageReveal>
          ))}
        </div>
      </section>

      {/* ARTISTS HIGHLIGHT */}
      <section className="relative bg-black text-white grain">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10 py-24 md:py-32">
          <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
            <ImageReveal className="relative">
              <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                <img src={ARTIST_IMG} alt="Drummer" className="h-full w-full object-cover grayscale transition-all duration-700 hover:grayscale-0" />
              </div>
              <span className="absolute -bottom-4 -right-4 bg-gavra px-5 py-3 font-mono-hw text-[11px] font-bold uppercase tracking-widest text-white">
                Trusted on stage
              </span>
            </ImageReveal>

            <div>
              <Reveal>
                <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">{h.artists_eyebrow}</p>
                <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase leading-none tracking-tight md:text-6xl">
                  {(h.artists_title || "").split("\n").map((ln, i) => (
                    <span key={i} className="block">{ln}</span>
                  ))}
                </h2>
                <p className="mt-6 max-w-md text-base leading-relaxed text-white/60">
                  {h.artists_description}
                </p>
                <Link
                  to="/artists"
                  data-testid="home-artists-btn"
                  className="mt-8 inline-flex items-center gap-3 border border-white/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra hover:border-gavra"
                >
                  Meet the artists <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>

              <div className="mt-12 grid grid-cols-3 gap-4">
                {artists.map((a) => (
                  <Link key={a.id} to="/artists" className="group">
                    <div className="aspect-square overflow-hidden border border-white/10">
                      <img src={a.image_url} alt={a.name} className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0" />
                    </div>
                    <p className="mt-2 truncate font-mono-hw text-[10px] uppercase tracking-widest text-white/50 group-hover:text-gavra">{a.name}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
